import type { QueryClient } from '@tanstack/react-query';
import { api } from './api';
import { applyClubLanguage } from './clubLanguage';
import type { LegalNoticeStatus } from './legalNotice';

// One profile fetch shared by route guards (via ensureProfile) and components
// (via useQuery with the same key). Previously every navigation paid at least
// one raw-axios /auth/profile round-trip in beforeLoad — two for /admin and
// /logs — bypassing the cache Layout was already maintaining.

export const PROFILE_QUERY_KEY = ['my-profile'] as const;
export const PROFILE_STALE_TIME = 60_000;

export interface MyProfile {
  userId?: string;
  role?: string;
  tenantId?: string;
  clubId?: string;
  displayName?: string;
  email?: string;
  player?: {
    firstName?: string;
    lastName?: string;
    hebrewFirstName?: string | null;
    hebrewLastName?: string | null;
    imageUrl?: string | null;
  } | null;
  club?: {
    name?: string;
    logoUrl?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    language?: string | null;
    /** The club's GPS provider (Zero.Auth `clubs.gps_provider`). Absent from a backend older than Part 5. */
    gpsProvider?: 'catapult' | 'ksport';
    /** The club's live match-data provider (Zero.Auth `clubs.live_provider`). Absent from a backend older than it. */
    liveProvider?: 'opta' | 'sportradar';
  } | null;
  /** The one-time confidentiality notice (`lib/legalNotice.ts`). Absent from a Zero.Auth older than it. */
  legalNotice?: LegalNoticeStatus | null;
  [key: string]: unknown;
}

export async function fetchProfile(): Promise<MyProfile> {
  const { data } = await api.get('/auth/profile');
  return data;
}

/**
 * Route-guard entry point: returns the cached profile when fresh, otherwise
 * fetches once (with a single quick retry for service cold starts) and caches
 * it for components. Throws on failure — callers decide how to degrade.
 */
export async function ensureProfile(queryClient: QueryClient): Promise<MyProfile> {
  const profile = await queryClient.ensureQueryData({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: fetchProfile,
    staleTime: PROFILE_STALE_TIME,
    retry: 1,
    retryDelay: 2_000,
  });
  // Every route guard awaits this, so the language is correct BEFORE the route renders — no flash
  // of English on the way into a Hebrew club. The hook in Layout/PlayerProfilePage covers the
  // later re-applies (a club switch, or an admin saving a new language).
  await applyClubLanguage(profile.club?.language);
  return profile;
}
